import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatSort, MatSortHeader } from "@angular/material/sort";
import {MatPaginator} from "@angular/material/paginator";
import {merge, Subscription} from "rxjs";
import {debounceTime, tap} from "rxjs/operators";
import { FormControl, FormGroup, UntypedFormGroup, ReactiveFormsModule } from "@angular/forms";
import {
  BaseDatasource,
  BaseList,
  pageSizeDefault,
  pageSizeOptions,
  Pagination,
  SearchFieldGroup,
  SearchOperator
} from "@shared/base";
import {Relation} from '@app/relation/shared/relation';
import {Device} from "@app/device/shared/device";
import {DeviceApi} from "@app/device/shared/device.api";
import { MatToolbar } from '@angular/material/toolbar';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';

@Component({
    templateUrl: './device-select-dialog.component.html',
    styleUrls: ['./device-select-dialog.component.scss'],
    imports: [MatToolbar, CdkScrollable, MatDialogContent, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatIconButton, MatSuffix, MatIcon, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatDialogActions, MatPaginator, MatButton]
})
export class DeviceSelectDialogComponent extends BaseList<Device> implements OnInit, OnDestroy, AfterViewInit {
  dialogRef = inject<MatDialogRef<DeviceSelectDialogComponent>>(MatDialogRef);
  protected listApi = inject(DeviceApi);
  protected listCachePrefix = 'device-select';

  title: string;
  dataSource: BaseDatasource<Device>;
  displayedColumns = ['name', 'manufacturer'];
  filterForm: FormGroup;
  filteredOptions!: Subscription;
  searchField: boolean;

  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild('searchInput') searchInput!: ElementRef;

  constructor() {
    super();
    const data = inject(MAT_DIALOG_DATA);

    this.filterForm = new FormGroup({
      device: new FormControl<string | undefined>(undefined)
    });

    this.title = data.title;
  }

  ngOnInit() {
    super.ngOnInit();

    this.filteredOptions = this.filterForm.valueChanges.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.loadData();
    });
  }

  ngAfterViewInit() {
    merge(this.sort.sortChange, this.paginator.page).pipe(
      tap(() => {
        this.loadData();
      })
    ).subscribe();

    this.loadData();

    this.showSearch();
  }

  ngOnDestroy() {
    this.filteredOptions.unsubscribe();
    super.ngOnDestroy();
  }

  defaultListForm(): UntypedFormGroup {
    return this.formBuilder.group({device: null});
  }

  defaultPagination(): Pagination {
    return new Pagination(0, pageSizeDefault, 'name', 'asc');
  }

  prepareSearch(values: Record<string, string>): SearchFieldGroup {
    const deviceFilter = {
      'name': values.device ? `${values.device.trim()}` : null,
      'manufacturer.name': values.device ? `${values.device.trim()}` : null
    };

    const deviceGroup: SearchFieldGroup = {
      type: 'OR',
      fields: [],
      groups: []
    };

    const deviceProperties = Object.keys(deviceFilter);
    deviceProperties.forEach(value => {
      if(deviceFilter[value] !== null) {
        deviceGroup.fields.push({
          fieldName: value,
          operator: SearchOperator.CONTAINS,
          value: deviceFilter[value]
        });
      }
    });
    const group: SearchFieldGroup = {
      type: 'AND',
      fields: [],
      groups: [deviceGroup]
    };

    return group;
  }

  showSearch() {
    this.searchField = !this.searchField;

    //Otherwise the element isn't visible and the focus will not work
    setTimeout(()=>{
      this.searchInput.nativeElement.focus();
    }, 250);
  }

  select(relation: Relation) {
    this.dialogRef.close(relation);
  }

  close() {
    this.dialogRef.close(false);
  }

  protected readonly pageSizeOptions = pageSizeOptions;
}
